import React, { FC } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button } from '@bphxd/ds-core-react';
import { EMPTY_PROCEDURE } from '../../../lib/constants';
import { TProcedure } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { PROCEDURE_CATEGORY_ENUM, PROCEDURE_STATUS_ENUM } from '../../../lib/constants/dbConstants';
import { renderOptions } from '../../../lib/util/render';
import {
  ProcedureCategoryEnum,
  ProcedureStatusEnum,
} from '../../../graph-queries/__generated__/graphql';

const ProcedureForm: FC<{
  defaultValues?: TProcedure;
  onSubmit: (procedure: TProcedure) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, type, onClose, isLoading }) => {
  const asset = useSelectedAsset();
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string> & {
      category: ProcedureCategoryEnum;
      status: ProcedureStatusEnum;
    };

    const procedure: TProcedure = {
      ...EMPTY_PROCEDURE,
      ...inputData,
      assetId: asset?.assetId || -1,
      procedureId: parseInt(inputData.procedureId),
    };
    if (defaultValues?.procedureId) procedure.procedureId = defaultValues.procedureId;

    onSubmit(procedure)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };
  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-12">
        <FormGroup>
          <Label for="procedure-number">Procedure Number</Label>
          <Input
            type="text"
            name="procedureNumber"
            id="procedure-number"
            required
            defaultValue={defaultValues?.procedureNumber || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="title">Title</Label>
          <Input type="text" name="title" id="title" defaultValue={defaultValues?.title || ''} />
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="category">Category</Label>
          <Input
            type="select"
            name="category"
            id="category"
            className="form-select-placeholder-hack"
            role="combobox"
            defaultValue={defaultValues?.category || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(PROCEDURE_CATEGORY_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="url">URL</Label>
          <Input type="text" name="url" id="url" defaultValue={defaultValues?.url || ''} />
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="procedure-status">Status</Label>
          <Input
            type="select"
            name="status"
            id="procedure-status"
            required
            className="form-select-placeholder-hack"
            role="combobox"
            defaultValue={defaultValues?.status || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(PROCEDURE_STATUS_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="procedure-comments" className="label-optional">
            Comments
          </Label>
          <Input
            type="textarea"
            name="comments"
            id="procedure-comments"
            defaultValue={defaultValues?.comments || ''}
          />
        </FormGroup>
      </div>
      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading}
          >
            Add new procedure
          </Button>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading}
            >
              Save changes
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default ProcedureForm;
